import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import type {
  HTMLProps,
  JSX,
  PropsWithChildren,
  ReactNode,
  Ref,
  RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { clsx } from 'clsx'
import IsolatedStyleScope, {
  IsolatedStyleScopeContext,
} from '../../shared/IsolatedStyleScope'
import Context from '../../shared/Context'
import { getThemeClasses } from '../../shared/Theme'

import { useIsomorphicLayoutEffect as useLayoutEffect } from '../../shared/helpers/useIsomorphicLayoutEffect'

type SelectorOptions = {
  /**
   * The id used for the portal root element. Defaults to `eufemia-portal-root`.
   * If an element with this id already exists in the DOM, it will be reused.
   */
  id?: string
  /**
   * CSS selector for a container to place the portal root inside. The portal element is inserted as the first child of the matched element.
   */
  insideSelector?: string
  /**
   * CSS selector for a target element; the portal root will be inserted directly before the first matched element.
   */
  beforeSelector?: string
}

export type PortalRootProps = {
  ref?:
    | Ref<HTMLElement>
    | RefObject<HTMLElement>
    | ((instance: HTMLElement) => void)
} & SelectorOptions &
  Omit<HTMLProps<HTMLElement>, 'ref' | 'id'>

type PortalRootContextValue = SelectorOptions & {
  portalAttributes?: Record<string, unknown>
}

const PortalRootContext = createContext<PortalRootContextValue | null>(
  null
)

export type PortalRootProviderProps = PropsWithChildren<
  SelectorOptions & Omit<HTMLProps<HTMLElement>, 'id' | 'children' | 'ref'>
>

export function PortalRootProvider(
  props: PortalRootProviderProps
): JSX.Element | null {
  const { id, insideSelector, beforeSelector, children, ...rest } = props

  const hasRest = Object.keys(rest).length > 0
  const restKey = hasRest ? JSON.stringify(rest) : ''

  const value = useMemo(
    () => ({
      id,
      insideSelector,
      beforeSelector,
      ...(hasRest ? { portalAttributes: rest } : undefined),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, insideSelector, beforeSelector, restKey]
  )

  return <PortalRootContext value={value}>{children}</PortalRootContext>
}

function PortalRootComponent(props: PortalRootProps = {}): ReactNode {
  const {
    id: idProp,
    insideSelector: insideSelectorProp,
    beforeSelector: beforeSelectorProp,
    ref: refProp,
    className,
    style,
    children,
    ...rest
  } = props

  const [, forceUpdate] = useReducer(() => ({}), {})
  const { style: scopeStyle } = useContext(IsolatedStyleScopeContext) || {}
  const selectorContext = useContext(PortalRootContext)
  const context = useContext(Context)

  const hasPropOverride =
    typeof insideSelectorProp !== 'undefined' ||
    typeof beforeSelectorProp !== 'undefined'

  const insideSelector = hasPropOverride
    ? insideSelectorProp
    : selectorContext?.insideSelector
  const beforeSelector = hasPropOverride
    ? beforeSelectorProp
    : selectorContext?.beforeSelector

  const effectiveId =
    idProp ?? selectorContext?.id ?? 'eufemia-portal-root'

  // Get initial element for first render - may not find HTML element with id yet
  const initialElement = useMemo(() => {
    if (idProp && typeof document !== 'undefined') {
      return document.getElementById(idProp)
    }
    return getOrCreatePortalElement({
      id: effectiveId,
      insideSelector,
      beforeSelector,
    })
  }, [effectiveId, idProp, insideSelector, beforeSelector])
  const localRef = useRef<HTMLElement | null>(initialElement)

  useLayoutEffect(() => {
    if (idProp || insideSelector || beforeSelector) {
      const elem = getOrCreatePortalElement({
        id: effectiveId,
        insideSelector,
        beforeSelector,
      })
      if (localRef.current !== elem) {
        localRef.current = elem
        forceUpdate()
      }
    }

    if (refProp && localRef.current) {
      if (typeof refProp === 'function') {
        refProp(localRef.current)
      } else {
        const ref = refProp as RefObject<HTMLElement | null>
        ref.current = localRef.current
      }
    }
  }, [effectiveId, idProp, refProp, insideSelector, beforeSelector])

  const portalElement = localRef.current || initialElement
  if (!portalElement) {
    return null
  }

  return createPortal(
    <IsolatedStyleScope
      scopeHash="auto"
      disableCoreStyleWrapper
      uniqueKey={false} // ensure that the scope is used on every portal root
    >
      <div
        className={clsx(
          'dnb-core-style',
          'eufemia-portal-root',
          getThemeClasses(context?.theme),
          className
        )}
        style={{ ...scopeStyle, ...style }}
        {...selectorContext?.portalAttributes}
        {...rest}
      >
        {children}
      </div>
    </IsolatedStyleScope>,
    portalElement
  )
}

export function getOrCreatePortalElement({
  id,
  insideSelector,
  beforeSelector,
}: SelectorOptions): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null
  }

  let elem = document.getElementById(id)

  if (!elem) {
    // Determine insertion point
    let parent: ParentNode & Node = document.body
    let referenceNode: ChildNode | null = document.body.firstChild

    if (beforeSelector) {
      const target = document.querySelector(beforeSelector)
      if (target && target.parentElement) {
        parent = target.parentElement
        referenceNode = target
      }
    } else if (insideSelector) {
      const target = document.querySelector(insideSelector)
      if (target) {
        parent = target
        referenceNode = target.firstChild
      }
    }

    elem = document.createElement('div')
    elem.setAttribute('id', id)
    parent.insertBefore(elem, referenceNode)
  }

  // Ensure role attribute
  if (!elem.hasAttribute('role')) {
    elem.setAttribute('role', 'presentation')
  }

  return elem
}
function PortalRoot(props: PortalRootProps) {
  return <PortalRootComponent {...props} />
}
PortalRoot.Provider = PortalRootProvider

export default PortalRoot
